import { type ComponentType, type ReactNode } from 'react';
import {
  AlertTriangle,
  Inbox,
  RefreshCw,
  type LucideProps,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { Button } from './button';

/**
 * The three states every async panel in the portal owes the user:
 * `Skeleton` while loading, `ErrorState` when the fetch failed, `EmptyState`
 * when it succeeded with nothing in it.
 *
 * The distinction matters: before these existed a failed request and an empty
 * result both rendered as a blank panel, so "this repo has no compliance
 * report" was indistinguishable from "GitHub returned 403"
 * (see docs/ux-design-review.md §3).
 */

type StateShellProps = {
  icon: ComponentType<LucideProps>;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  tone?: 'muted' | 'danger';
};

const StateShell = ({
  icon: Icon,
  title,
  description,
  action,
  className,
  tone = 'muted',
}: StateShellProps) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border px-6 py-10 text-center',
      tone === 'danger' &&
        'border-solid border-destructive/40 bg-destructive-subtle',
      className,
    )}
  >
    <Icon
      aria-hidden="true"
      className={cn(
        'size-8 shrink-0',
        tone === 'danger' ? 'text-destructive-strong' : 'text-muted-foreground',
      )}
    />
    <div className="flex flex-col gap-1">
      <p
        className={cn(
          'text-sm font-semibold',
          tone === 'danger' ? 'text-destructive-strong' : 'text-foreground',
        )}
      >
        {title}
      </p>
      {description && (
        <div className="mx-auto max-w-prose text-sm text-muted-foreground">
          {description}
        </div>
      )}
    </div>
    {action}
  </div>
);

export type EmptyStateProps = {
  /** What is missing, e.g. "No compliance report yet". */
  title: string;
  /** Why it is missing and what the user can do about it. */
  description?: ReactNode;
  icon?: ComponentType<LucideProps>;
  action?: ReactNode;
  className?: string;
  /** `danger` when what's missing is something that broke, not just nothing yet. */
  tone?: 'muted' | 'danger';
};

export const EmptyState = ({
  title,
  description,
  icon,
  action,
  className,
  tone = 'muted',
}: EmptyStateProps) => (
  <StateShell
    icon={icon ?? (tone === 'danger' ? AlertTriangle : Inbox)}
    title={title}
    description={description}
    action={action}
    className={className}
    tone={tone}
  />
);

export type ErrorStateProps = {
  /** What failed, in the user's terms, e.g. "Could not load compliance findings". */
  title?: string;
  /** The underlying failure. Its message is shown — never swallow it. */
  error?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
};

/**
 * Optional fields a thrown error may carry to steer how it is presented.
 * Read structurally so producers (e.g. `githubApiError` in
 * `@vivicta/github-config`) don't have to depend on this package.
 */
export type ErrorPresentation = {
  /** Cause and next step for whoever runs the portal. Kept behind a disclosure. */
  detail?: string;
  /** When false, the same request cannot succeed, so no retry is offered. */
  retryable?: boolean;
};

export const describeError = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return 'No further detail was returned.';
};

const presentationOf = (error: unknown): ErrorPresentation => {
  if (typeof error !== 'object' || error === null) return {};
  const { detail, retryable } = error as ErrorPresentation;
  return {
    detail: typeof detail === 'string' ? detail : undefined,
    retryable: typeof retryable === 'boolean' ? retryable : undefined,
  };
};

export const ErrorState = ({
  title = 'Something went wrong',
  error,
  onRetry,
  retryLabel = 'Try again',
  className,
}: ErrorStateProps) => {
  const { detail, retryable } = presentationOf(error);
  return (
    <StateShell
      tone="danger"
      icon={AlertTriangle}
      title={title}
      description={
        <>
          <p>{describeError(error)}</p>
          {detail && (
            <details className="mt-2 text-xs [&_summary]:cursor-pointer">
              <summary className="font-medium underline-offset-2 hover:underline">
                What caused this
              </summary>
              <p className="mt-1.5 first-letter:uppercase">{detail}</p>
            </details>
          )}
        </>
      }
      className={className}
      action={
        // A retry control that cannot possibly work is worse than none: it
        // invites the user to keep clicking at an operator-side problem.
        onRetry &&
        retryable !== false && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw aria-hidden="true" />
            {retryLabel}
          </Button>
        )
      }
    />
  );
};
